import React, { useState, useEffect } from 'react';
import { FileSearch, CheckCircle2, AlertTriangle, Clock, ChevronDown, ChevronRight, Upload, Search } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { apiGet, apiPost, apiUpload } from '../../../utils/api';
import AuditFileSelectionModal from '../AuditFileSelectionModal';
import ReviewPipelineUploadModal from '../ReviewPipelineUploadModal';
import SmartFilter from '../SmartFilter';
import toast from 'react-hot-toast';

const TF_STATUS = {
  pending:                { label: 'Pending', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  file_received:          { label: 'Received', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  in_review:              { label: 'In Review', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  corrections_needed:     { label: 'Corrections Needed', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  corrections_requested:  { label: 'Corrections Requested', cls: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300' },
  corrections_received:   { label: 'Corrections Received', cls: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' },
  provisional_approval:   { label: 'Provisional', cls: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300' },
  final_approval:         { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  completed:              { label: 'Completed', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
};

const ReviewPipelineTab = ({ auditId }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProp, setExpandedProp] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPropertyForAction, setSelectedPropertyForAction] = useState(null);
  const [showPropertyFileSelection, setShowPropertyFileSelection] = useState(false);
  const [showUploadModal, setShowStatusReportImport] = useState(false);

  const handleSinglePropertyUpload = async (propertyId, file) => {
    try {
      const formData = new FormData();
      formData.append('files[]', file);
      const res = await apiUpload(`/admin/audit_tracker/audits/${auditId}/import_status_reports`, formData);
      if (res.status === 202) {
        const { task_id } = await res.json();
        toast.success('Parsing status report...');
        const poll = setInterval(async () => {
          const sr = await apiGet(`/admin/audit_tracker/audits/${auditId}/import_status_reports_status/${task_id}`);
          if (sr.ok) {
            const sd = await sr.json();
            if (sd.status === 'done') {
              clearInterval(poll);
              if (sd.result?.success) {
                const cr = await apiPost(`/admin/audit_tracker/audits/${auditId}/import_status_reports_confirm`, { properties: sd.result.properties });
                if (cr.ok) { toast.success('Status report imported'); loadData(); }
              } else { toast.error(sd.result?.error || 'Parse failed'); }
            }
          }
        }, 2000);
      }
    } catch (err) { toast.error(err.message); }
  };

  const loadData = () => {
    if (!auditId) return;
    setLoading(true);
    apiGet(`/admin/audit_tracker/audits/${auditId}/review_pipeline`)
      .then(async (r) => { if (r.ok) setData(await r.json()); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [auditId]);


  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  const empty = !data || (data.summary?.total_files === 0 && data.properties?.length === 0);
  if (empty) {
    return (
      <div className={`text-center py-16 rounded-xl border ${d ? 'border-gray-700' : 'border-gray-200'}`}>
        <FileSearch size={40} className={`mx-auto mb-4 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
        <p className={`text-lg font-dm-serif mb-2 ${d ? 'text-gray-300' : 'text-gray-600'}`}>No Tenant Files in Review</p>
        <p className={`text-[13px] font-rasa mb-4 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
          Files appear here automatically from the processor mirror once the client has review activity this year.
          For non-processor clients, upload status reports instead.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setShowStatusReportImport(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-rasa ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
          >
            <Upload size={15} /> Upload status reports
          </button>
        </div>
        <ReviewPipelineUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowStatusReportImport(false)}
          auditId={auditId}
          existingProperties={[]}
          onImported={loadData}
        />
      </div>
    );
  }

  const { summary, properties, corrections_pending } = data;

  // Filter properties
  const filtered = (properties || []).filter((p) => {
    if (search && !p.property_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter === 'corrections' && !p.tenant_files?.some((tf) => ['corrections_needed', 'corrections_requested'].includes(tf.status))) return false;
    // Statuses come from the mirror (pending/in_review/.../completed) OR the
    // upload path (file_received, corrections_*) — filters match the union.
    if (statusFilter === 'approved' && !p.tenant_files?.some((tf) => ['final_approval', 'completed'].includes(tf.status))) return false;
    if (statusFilter === 'pending' && !p.tenant_files?.some((tf) => ['pending', 'file_received', 'in_review'].includes(tf.status))) return false;
    return true;
  });

  const Badge = ({ status }) => {
    const cfg = TF_STATUS[status] || TF_STATUS.pending;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${cfg.cls}`}>{cfg.label}</span>;
  };

  return (
    <div className="space-y-6">

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Files', value: summary.total_files, color: '' },
          { label: 'Awaiting / In Review', value: (summary.statuses?.pending || 0) + (summary.statuses?.in_review || 0), color: 'text-blue-500' },
          { label: 'Reviewed', value: summary.reviewed || 0, color: 'text-violet-500' },
          { label: 'Completed', value: summary.statuses?.completed || 0, color: 'text-emerald-500' },
          { label: 'Properties in Review', value: summary.properties_in_review, color: '' },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl p-4 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <div className={`text-xs font-bold uppercase tracking-widest font-rasa mb-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
            <div className={`text-2xl font-dm-serif ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Corrections Tracker ── */}
      {corrections_pending?.length > 0 && (
        <div className={`rounded-xl p-5 ${d ? 'bg-orange-900/10 border border-orange-800/30' : 'bg-orange-50 border border-orange-200'} shadow-sm`}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={15} className="text-orange-500" />
            <span className={`text-xs font-bold uppercase tracking-widest font-rasa ${d ? 'text-orange-300' : 'text-orange-600'}`}>
              Corrections Needed ({corrections_pending.length})
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {corrections_pending.slice(0, 8).map((c) => (
              <div key={c.id} className={`flex items-center justify-between p-2.5 rounded-lg ${d ? 'bg-gray-800/50' : 'bg-white'}`}>
                <div>
                  <div className={`text-[13px] font-rasa font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>{c.tenant_name}</div>
                  <div className={`text-xs font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                    {c.property_name} · Unit {c.unit || '-'} · Round {c.round}
                    {c.days_waiting && <span className={c.days_waiting > 14 ? ' text-red-500 font-bold' : ''}> · {c.days_waiting}d waiting</span>}
                  </div>
                </div>
                <Badge status={c.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Smart Filter ── */}
      <SmartFilter
        audience="internal"
        context="review"
        onApplyFilters={(aiFilters) => {
          if (aiFilters.search) setSearch(aiFilters.search);
          if (aiFilters.quick === 'corrections' || aiFilters.status?.includes('correction')) setStatusFilter('corrections');
          else if (aiFilters.status === 'final_approval') setStatusFilter('approved');
          else if (Object.keys(aiFilters).length === 0) { setSearch(''); setStatusFilter(''); }
        }}
      />

      {/* ── Filter Bar + Upload ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search property..."
            className={`w-full pl-8 pr-3 py-2 rounded-lg border text-[13px] font-rasa ${d ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
          />
        </div>
        <div className={`flex rounded-lg border overflow-hidden ${d ? 'border-gray-700' : 'border-gray-300'}`}>
          {[
            { key: '', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'corrections', label: 'Corrections' },
            { key: 'approved', label: 'Approved' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 text-[13px] font-rasa font-medium transition-colors ${
                statusFilter === f.key
                  ? (d ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                  : (d ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-500 hover:bg-gray-50')
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button
          onClick={() => setShowStatusReportImport(true)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-rasa font-medium ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
        >
          <Upload size={14} /> Upload Reports
        </button>
      </div>

      {/* ── Property-grouped File Table ── */}
      <div className={`rounded-xl border overflow-hidden ${d ? 'border-gray-700' : 'border-gray-200'} shadow-sm`}>
        {filtered.length === 0 ? (
          <div className={`text-center py-12 text-[13px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>No properties match filters</div>
        ) : filtered.map((prop) => {
          const isOpen = expandedProp === prop.id;
          const tfs = prop.tenant_files || [];
          const approved = tfs.filter((tf) => ['final_approval', 'completed'].includes(tf.status)).length;
          const corrections = tfs.filter((tf) => ['corrections_needed', 'corrections_requested'].includes(tf.status)).length;

          return (
            <div key={prop.id}>
              {/* Property header row */}
              <button
                onClick={() => setExpandedProp(isOpen ? null : prop.id)}
                className={`w-full flex items-center justify-between px-5 py-3 border-b transition-colors ${
                  d ? 'border-gray-700 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  <div className="text-left">
                    <div className={`text-[13px] font-rasa font-bold ${d ? 'text-gray-200' : 'text-gray-700'}`}>{prop.property_name}</div>
                    <div className={`text-xs font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                      {tfs.length} files · {approved} approved
                      {corrections > 0 && <span className="text-orange-500"> · {corrections} corrections</span>}
                      {prop.avg_cycle_days && <span> · {prop.avg_cycle_days}d avg cycle</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Mini progress bar */}
                  <div className={`w-24 h-1.5 rounded-full ${d ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className="h-1.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${tfs.length > 0 ? (approved / tfs.length * 100) : 0}%` }} />
                  </div>
                  <span className={`text-xs font-rasa font-medium ${d ? 'text-gray-400' : 'text-gray-500'}`}>{approved}/{tfs.length}</span>
                </div>
              </button>

              {/* Expanded tenant files */}
              {isOpen && (
                <div className={`px-5 py-3 ${d ? 'bg-gray-800/30' : 'bg-gray-50/50'} border-b ${d ? 'border-gray-700' : 'border-gray-100'}`}>
                  {/* Per-property actions */}
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => {
                        setSelectedPropertyForAction(prop);
                        setShowPropertyFileSelection(true);
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-rasa font-medium bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      <FileSearch size={12} /> Run File Selection
                    </button>
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.xls,.xlsx';
                        input.onchange = (e) => {
                          if (e.target.files?.[0]) handleSinglePropertyUpload(prop.id, e.target.files[0]);
                        };
                        input.click();
                      }}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-rasa font-medium ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                    >
                      <Upload size={12} /> Upload Status Report
                    </button>
                  </div>
                  <table className="w-full text-[13px] font-rasa">
                    <thead>
                      <tr>
                        {['Tenant', 'Unit', 'Status', 'Received', 'Complete', 'Auditor', 'Documents'].map((h) => (
                          <th key={h} className={`px-3 py-1.5 text-left font-bold uppercase text-xs tracking-wider ${d ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tfs.map((tf) => (
                        <tr key={tf.id} className={`border-b ${d ? 'border-gray-700/30' : 'border-gray-100'}`}>
                          <td className={`px-3 py-2 font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>{tf.tenant_name}</td>
                          <td className={`px-3 py-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.unit || '-'}</td>
                          <td className="px-3 py-2"><Badge status={tf.status} /></td>
                          <td className={`px-3 py-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.file_received_date || '-'}</td>
                          <td className={`px-3 py-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.final_approval_date || tf.complete_date || '-'}</td>
                          <td className={`px-3 py-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf.auditor || '-'}</td>
                          <td className="px-3 py-2">
                            {(() => {
                              const needed = Number(tf.documents_needed || 0);
                              const received = Number(tf.documents_received || 0);
                              const done = Number(tf.documents_completed || 0);
                              if (needed <= 0) return <span className={d ? 'text-gray-600' : 'text-gray-300'}>-</span>;
                              return (
                                <span
                                  className={`text-[11px] ${received < needed ? 'text-orange-500 font-bold' : (d ? 'text-gray-400' : 'text-gray-500')}`}
                                  title={(tf.documents || []).map((doc) => `${doc.type || 'doc'}${doc.received_date ? ' ✓ received' : ' • awaiting'}${doc.completed_date ? ' • done' : ''}`).join('\n')}
                                >
                                  {received}/{needed} received
                                  {done > 0 && <span className={d ? 'text-gray-500' : 'text-gray-400'}> · {done} done</span>}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* File Selection Modal */}
      <AuditFileSelectionModal
        isOpen={showPropertyFileSelection}
        onClose={() => { setShowPropertyFileSelection(false); setSelectedPropertyForAction(null); }}
        property={selectedPropertyForAction}
        onSelectionComplete={() => { loadData(); setShowPropertyFileSelection(false); }}
      />

      {/* Status Report Import Modal */}
      <ReviewPipelineUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowStatusReportImport(false)}
        auditId={auditId}
        existingProperties={data?.properties || []}
        onImported={loadData}
      />
    </div>
  );
};

export default ReviewPipelineTab;
